import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import './CustomerAuth.css';

// Helper function to get the correct API base URL
const getApiUrl = (endpoint) => {
  // Use proxy for API calls
  return `/api${endpoint}`;
};

const CustomerAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [availableStores, setAvailableStores] = useState([]);
  const [loginCredentials, setLoginCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login: contextLogin, register: contextRegister, isAuthenticated, loading: authLoading, checkAuthInOtherTabs } = useCustomerAuth();

  // Get store slug and redirect URL from URL params or location state
  // storeSlug is now optional - backend will auto-discover from customer email
  const storeSlug = new URLSearchParams(location.search).get('store') ||
                   location.state?.storeSlug || null;
  const redirectUrl = new URLSearchParams(location.search).get('redirect') ||
                     location.state?.redirect || '/dashboard';
  const wishlistProductId = new URLSearchParams(location.search).get('wishlist') || null;

  // Debug logging
  console.log('CustomerAuth: Current URL:', window.location.href);
  console.log('CustomerAuth: Location search:', location.search);
  console.log('CustomerAuth: Redirect URL extracted:', redirectUrl);
  console.log('CustomerAuth: Store slug:', storeSlug);
  console.log('CustomerAuth: Wishlist product ID:', wishlistProductId);
  console.log('CustomerAuth: Is authenticated:', isAuthenticated);
  console.log('CustomerAuth: Auth loading:', authLoading);

  /**
   * Redirect if already authenticated (with cross-tab detection)
   * This ensures users don't see the login page when already logged in
   */
  useEffect(() => {
    // FIXED: Only redirect if auth is fully loaded AND user is authenticated
    // Add a small delay to prevent race conditions with auth initialization
    if (!authLoading && isAuthenticated) {
      console.log('CustomerAuth: User already authenticated, preparing redirect to:', redirectUrl);

      const timer = setTimeout(async () => {
        // Double-check authentication state after delay
        if (isAuthenticated) {
          console.log('CustomerAuth: Confirmed authenticated, redirecting to:', redirectUrl);

          // Check if redirectUrl is a full URL (external redirect)
          if (redirectUrl.startsWith('http')) {
            console.log('CustomerAuth: External URL redirect via window.location');

            // CRITICAL FIX: For storefront URLs, append sessionId
            const merchantBase = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
            const customerBase = process.env.REACT_APP_CUSTOMER_BASE_URL || 'http://localhost:3001';
            const isCustomerAppUrl = redirectUrl.startsWith(customerBase);

            if (!isCustomerAppUrl) {
              // This is a storefront URL - get sessionId and append it
              console.log('🔵 CustomerAuth: Storefront URL detected (already auth), fetching sessionId...');

              try {
                // Fetch current session token from backend
                const response = await fetch(getApiUrl('/customer/auth/refresh'), {
                  method: 'POST',
                  credentials: 'include'
                });

                if (response.ok) {
                  const data = await response.json();
                  const sessionId = data.sessionId || data.token;

                  if (sessionId) {
                    const separator = redirectUrl.includes('?') ? '&' : '?';
                    const redirectUrlWithSession = `${redirectUrl}${separator}sessionId=${sessionId}`;
                    console.log('✅ CustomerAuth: Already auth - Full redirect URL:', redirectUrlWithSession);
                    window.location.href = redirectUrlWithSession;
                    return;
                  } else {
                    console.warn('⚠️ CustomerAuth: No sessionId in refresh response');
                  }
                } else {
                  console.error('❌ CustomerAuth: Failed to fetch session token:', response.status);
                }
              } catch (error) {
                console.error('❌ CustomerAuth: Error fetching session token:', error);
              }
            }

            // Fallback: redirect without sessionId
            window.location.href = redirectUrl;
          } else if (redirectUrl.startsWith('/customer/')) {
            // Convert legacy /customer/* paths
            const target = redirectUrl.replace('/customer/', '/');
            console.log('CustomerAuth: Internal redirect (converted from /customer/*) to:', target);
            navigate(target, { replace: true });
          } else {
            // Internal route
            console.log('CustomerAuth: Internal redirect to:', redirectUrl);
            navigate(redirectUrl, { replace: true });
          }
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, authLoading, redirectUrl, navigate]);
  

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Billing address fields
    billingAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Nigeria'
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested billing address fields
    if (name.startsWith('billingAddress.')) {
      const fieldName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [fieldName]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        await handleLogin();
      } else {
        await handleRegister();
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to add product to wishlist
  const addToWishlist = async (productId) => {
    try {
      console.log('CustomerAuth: Adding product to wishlist:', productId);
      const response = await fetch(getApiUrl('/customer/wishlist/toggle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('CustomerAuth: Product added to wishlist:', data);
        return true;
      } else {
        console.error('CustomerAuth: Failed to add product to wishlist');
        return false;
      }
    } catch (error) {
      console.error('CustomerAuth: Wishlist error:', error);
      return false;
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      const { email, password } = formData;

      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      console.log('CustomerAuth: Attempting login with:', { email, storeSlug });
      const result = await contextLogin(email, password, storeSlug);

      // Check for multiple stores response
      if (result.multipleStores) {
        console.log('CustomerAuth: Multiple stores detected:', result.stores);
        setLoginCredentials({ email, password });
        setAvailableStores(result.stores);
        setShowStoreSelection(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        // Check if there's a product to add to wishlist
        if (wishlistProductId) {
          const added = await addToWishlist(wishlistProductId);
          if (added) {
            setSuccess('Login successful! Product added to your wishlist. Redirecting...');
          } else {
            setSuccess('Login successful! Redirecting...');
          }
        } else {
          setSuccess('Login successful! Redirecting...');
        }

        // CRITICAL DEBUG: Log the full result object
        console.log('🔵 CustomerAuth: Login result:', result);
        console.log('🔵 CustomerAuth: result.data:', result.data);
        console.log('🔵 CustomerAuth: result.data.sessionId:', result.data.sessionId);
        console.log('🔵 CustomerAuth: result.data.token:', result.data.token);

        // Redirect to the specified URL or default to dashboard
        setTimeout(() => {
          console.log('🔵 CustomerAuth: About to redirect. redirectUrl:', redirectUrl);
          console.log('🔵 CustomerAuth: redirectUrl.startsWith("http"):', redirectUrl.startsWith('http'));
          console.log('🔵 CustomerAuth: redirectUrl.startsWith("/s/"):', redirectUrl.startsWith('/s/'));
          console.log('🔵 CustomerAuth: redirectUrl.startsWith("/checkout"):', redirectUrl.startsWith('/checkout'));

          const merchantBase = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
          const customerBase = process.env.REACT_APP_CUSTOMER_BASE_URL || 'http://localhost:3001';

          console.log('🔵 CustomerAuth: merchantBase:', merchantBase);
          console.log('🔵 CustomerAuth: customerBase:', customerBase);

          // Use sessionId or token (they're the same)
          const sessionId = result.data.sessionId || result.data.token;
          console.log('🔵 CustomerAuth: Using sessionId:', sessionId ? 'YES' : 'NO');

          if (redirectUrl.startsWith('http')) {
            // Check if it's a storefront URL (not customer app URL)
            const isCustomerAppUrl = redirectUrl.startsWith(customerBase);

            console.log('🔵 CustomerAuth: Is customer app URL?', isCustomerAppUrl);

            if (!isCustomerAppUrl) {
              // Storefront URL (subdomain, custom domain, or main merchant app)
              console.log('🔵 CustomerAuth: Storefront URL detected, appending sessionId');
              if (sessionId) {
                const separator = redirectUrl.includes('?') ? '&' : '?';
                const redirectUrlWithSession = `${redirectUrl}${separator}sessionId=${sessionId}`;
                console.log('✅ CustomerAuth: Full redirect URL:', redirectUrlWithSession);
                window.location.href = redirectUrlWithSession;
              } else {
                console.warn('⚠️ CustomerAuth: No sessionId, redirecting without session');
                window.location.href = redirectUrl;
              }
            } else {
              // Customer app URL, redirect directly (shouldn't happen normally)
              console.log('🔵 CustomerAuth: Customer app URL redirect to:', redirectUrl);
              window.location.href = redirectUrl;
            }
          } else if (redirectUrl.startsWith('/s/') || redirectUrl.startsWith('/checkout') || redirectUrl.startsWith('/product/')) {
            // Store URL, checkout, or product page - redirect to main store with session ID
            console.log('🔵 CustomerAuth: Redirecting to main store with sessionId');
            const base = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
            if (sessionId) {
              // Check if redirectUrl already has query params
              const separator = redirectUrl.includes('?') ? '&' : '?';
              const redirectUrlWithSession = `${base}${redirectUrl}${separator}sessionId=${sessionId}`;
              console.log('✅ CustomerAuth: Full redirect URL:', redirectUrlWithSession);
              window.location.href = redirectUrlWithSession;
            } else {
              console.warn('⚠️ CustomerAuth: No sessionId, redirecting without session');
              window.location.href = `${base}${redirectUrl}`;
            }
          } else {
            // Internal route, use navigate
            let internalTarget = redirectUrl;

            // Handle common redirect patterns - convert legacy /customer/* paths
            if (redirectUrl.startsWith('/customer/')) {
              // Convert /customer/* paths to their direct equivalents
              internalTarget = redirectUrl.replace('/customer/', '/');
            }

            console.log('🔵 CustomerAuth: Internal route redirect to:', internalTarget);
            navigate(internalTarget, { state: { storeSlug } });
          }
        }, 1500);
      } else {
        throw new Error(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('CustomerAuth: Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    const { firstName, lastName, email, phone, password, confirmPassword, billingAddress } = formData;

    if (!firstName || !lastName || !email || !phone || !password) {
      throw new Error('All fields are required');
    }

    if (password !== confirmPassword) {
      throw new Error('Passwords do not match');
    }

    if (password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    // Validate billing address
    if (!billingAddress.address1 || !billingAddress.city || !billingAddress.state || !billingAddress.postalCode) {
      throw new Error('Billing address is required');
    }

    const result = await contextRegister({
      firstName,
      lastName,
      email,
      phone,
      password,
      billingAddress
    }, storeSlug);

    if (result.success) {
      // Check if there's a product to add to wishlist
      if (wishlistProductId) {
        const added = await addToWishlist(wishlistProductId);
        if (added) {
          setSuccess('Registration successful! Product added to your wishlist. Redirecting...');
        } else {
          setSuccess('Registration successful! Please check your email to verify your account.');
        }
      } else {
        setSuccess('Registration successful! Please check your email to verify your account.');
      }

      if (result.data.sessionId) {
        try {
          // Don't store session data in localStorage for security
        } catch (e) {
          console.warn('Unable to persist sessionId to localStorage:', e);
        }
      }

      // Redirect to the specified URL or default to dashboard
      setTimeout(() => {
        const merchantBase = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
        const customerBase = process.env.REACT_APP_CUSTOMER_BASE_URL || 'http://localhost:3001';

        if (redirectUrl.startsWith('http')) {
          // Check if it's a storefront URL (not customer app URL)
          const isCustomerAppUrl = redirectUrl.startsWith(customerBase);

          if (!isCustomerAppUrl) {
            // Storefront URL (subdomain, custom domain, or main merchant app)
            console.log('CustomerAuth: Registration - Storefront URL detected, appending sessionId:', result.data.sessionId);
            if (result.data.sessionId) {
              const separator = redirectUrl.includes('?') ? '&' : '?';
              const redirectUrlWithSession = `${redirectUrl}${separator}sessionId=${result.data.sessionId}`;
              console.log('CustomerAuth: Registration - Full redirect URL:', redirectUrlWithSession);
              window.location.href = redirectUrlWithSession;
            } else {
              console.log('CustomerAuth: Registration - No sessionId, redirecting without session');
              window.location.href = redirectUrl;
            }
          } else {
            // Customer app URL, redirect directly (shouldn't happen normally)
            console.log('CustomerAuth: Registration - Customer app URL redirect to:', redirectUrl);
            window.location.href = redirectUrl;
          }
        } else if (redirectUrl.startsWith('/s/') || redirectUrl.startsWith('/checkout') || redirectUrl.startsWith('/product/')) {
          // Store URL, checkout, or product page - redirect to main store with session ID
          console.log('CustomerAuth: Registration - Redirecting to main store with sessionId:', result.data.sessionId);
          const base = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
          if (result.data.sessionId) {
            // Check if redirectUrl already has query params
            const separator = redirectUrl.includes('?') ? '&' : '?';
            const redirectUrlWithSession = `${base}${redirectUrl}${separator}sessionId=${result.data.sessionId}`;
            console.log('CustomerAuth: Registration - Full redirect URL:', redirectUrlWithSession);
            window.location.href = redirectUrlWithSession;
          } else {
            console.log('CustomerAuth: Registration - No sessionId, redirecting without session');
            window.location.href = `${base}${redirectUrl}`;
          }
        } else {
          // Internal route, use navigate
          const internalTarget = redirectUrl === '/customer/dashboard' ? '/dashboard' : redirectUrl;
          navigate(internalTarget, { state: { storeSlug } });
        }
      }, 2000);
    } else {
      throw new Error(result.error || 'Registration failed');
    }
  };

  const handleStoreSelect = async (selectedStoreSlug) => {
    try {
      setLoading(true);
      setError('');
      setShowStoreSelection(false);

      console.log('CustomerAuth: Store selected:', selectedStoreSlug);
      console.log('CustomerAuth: Re-authenticating with credentials and store...');

      // Re-call login with the selected store
      const result = await contextLogin(
        loginCredentials.email,
        loginCredentials.password,
        selectedStoreSlug
      );

      if (result.success) {
        // Check if there's a product to add to wishlist
        if (wishlistProductId) {
          const added = await addToWishlist(wishlistProductId);
          if (added) {
            setSuccess('Login successful! Product added to your wishlist. Redirecting...');
          } else {
            setSuccess('Login successful! Redirecting...');
          }
        } else {
          setSuccess('Login successful! Redirecting...');
        }

        // Redirect to the specified URL or default to dashboard
        setTimeout(() => {
          const merchantBase = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';

          if (redirectUrl.startsWith('http')) {
            // Check if it's a merchant platform URL
            if (redirectUrl.startsWith(merchantBase) || redirectUrl.includes('localhost:3000')) {
              // Merchant platform URL, append sessionId
              console.log('CustomerAuth: Store selection - Merchant platform URL, appending sessionId:', result.data.sessionId);
              if (result.data.sessionId) {
                const separator = redirectUrl.includes('?') ? '&' : '?';
                const redirectUrlWithSession = `${redirectUrl}${separator}sessionId=${result.data.sessionId}`;
                console.log('CustomerAuth: Store selection - Full redirect URL:', redirectUrlWithSession);
                window.location.href = redirectUrlWithSession;
              } else {
                console.log('CustomerAuth: Store selection - No sessionId, redirecting without session');
                window.location.href = redirectUrl;
              }
            } else {
              // External URL, redirect directly
              console.log('CustomerAuth: Store selection - External URL redirect to:', redirectUrl);
              window.location.href = redirectUrl;
            }
          } else if (redirectUrl.startsWith('/s/') || redirectUrl === '/checkout') {
            const base = process.env.REACT_APP_MERCHANT_BASE_URL || 'http://localhost:3000';
            if (result.data.sessionId) {
              window.location.href = `${base}${redirectUrl}?sessionId=${result.data.sessionId}`;
            } else {
              window.location.href = `${base}${redirectUrl}`;
            }
          } else {
            let internalTarget = redirectUrl;
            if (redirectUrl === '/customer/dashboard' || redirectUrl === '/customer/dashboard/') {
              internalTarget = '/dashboard/';
            } else if (redirectUrl.startsWith('/customer/')) {
              internalTarget = redirectUrl.replace('/customer', '');
            }
            navigate(internalTarget, { state: { storeSlug: selectedStoreSlug } });
          }
        }, 1500);
      } else {
        throw new Error(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('CustomerAuth: Store selection login error:', error);
      setError(error.message || 'Login failed. Please try again.');
      setShowStoreSelection(true); // Show selection again on error
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const { email } = formData;

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/customer-auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password reset link sent to your email');
      } else {
        throw new Error(data.error || 'Failed to send reset link');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-auth">
      <div className="auth-container">
        {showStoreSelection ? (
          // Store Selection UI
          <>
            <div className="auth-header">
              <h1>Select Your Store</h1>
              <p>
                Multiple accounts found with this email. Please select which store you want to log into.
              </p>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            <div className="store-selection-grid">
              {availableStores.map((store, index) => (
                <button
                  key={store.storeSlug || index}
                  className="store-card"
                  onClick={() => handleStoreSelect(store.storeSlug)}
                  disabled={loading}
                >
                  {store.storeLogo && (
                    <div className="store-logo">
                      <img src={store.storeLogo} alt={store.storeName} />
                    </div>
                  )}
                  <div className="store-details">
                    <h3>{store.storeName || 'Unnamed Store'}</h3>
                    {store.storeDescription && (
                      <p className="store-description">{store.storeDescription}</p>
                    )}
                    <span className="store-slug">@{store.storeSlug}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="auth-footer">
              <button
                type="button"
                onClick={() => {
                  setShowStoreSelection(false);
                  setAvailableStores([]);
                  setLoginCredentials({ email: '', password: '' });
                  setError('');
                }}
                className="link-button"
                disabled={loading}
              >
                Back to Login
              </button>
            </div>
          </>
        ) : (
          // Normal Auth UI
          <>
            <div className="auth-header">
              <h1>{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
              <p>
                {isLogin
                  ? 'Sign in to your account to continue'
                  : 'Create an account to manage your orders and preferences'
                }
              </p>
            </div>

            <div className="auth-tabs">
              <button
                className={`tab ${isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(true)}
              >
                Login
              </button>
              <button
                className={`tab ${!isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(false)}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required={!isLogin}
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required={!isLogin}
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Enter your phone number"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="pwd-field">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
                minLength={8}
              />
              <button
                type="button"
                className="pwd-reveal"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="pwd-field">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Confirm your password"
                  minLength={8}
                />
                <button
                  type="button"
                  className="pwd-reveal"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          {!isLogin && (
            <>
              <div className="form-section">
                <h3>Billing Address</h3>
                <p className="form-description">This will be used as your default billing address</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="billingAddress.address1">Address Line 1</label>
                <input
                  type="text"
                  id="billingAddress.address1"
                  name="billingAddress.address1"
                  value={formData.billingAddress.address1}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Street address, P.O. box, company name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="billingAddress.address2">Address Line 2 (Optional)</label>
                <input
                  type="text"
                  id="billingAddress.address2"
                  name="billingAddress.address2"
                  value={formData.billingAddress.address2}
                  onChange={handleInputChange}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billingAddress.city">City</label>
                  <input
                    type="text"
                    id="billingAddress.city"
                    name="billingAddress.city"
                    value={formData.billingAddress.city}
                    onChange={handleInputChange}
                    required={!isLogin}
                    placeholder="Enter city"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="billingAddress.state">State</label>
                  <input
                    type="text"
                    id="billingAddress.state"
                    name="billingAddress.state"
                    value={formData.billingAddress.state}
                    onChange={handleInputChange}
                    required={!isLogin}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billingAddress.postalCode">Postal Code</label>
                  <input
                    type="text"
                    id="billingAddress.postalCode"
                    name="billingAddress.postalCode"
                    value={formData.billingAddress.postalCode}
                    onChange={handleInputChange}
                    required={!isLogin}
                    placeholder="Enter postal code"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="billingAddress.country">Country</label>
                  <select
                    id="billingAddress.country"
                    name="billingAddress.country"
                    value={formData.billingAddress.country}
                    onChange={handleInputChange}
                    required={!isLogin}
                  >
                    <option value="Nigeria">Nigeria</option>
                    <option value="Ghana">Ghana</option>
                    <option value="Kenya">Kenya</option>
                    <option value="South Africa">South Africa</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {isLogin && (
            <div className="form-options">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="forgot-password"
                disabled={loading}
              >
                Forgot Password?
              </button>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                {isLogin ? 'Signing in...' : 'Creating account...'}
              </div>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="link-button"
            >
              {isLogin ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>

        {storeSlug && (
          <div className="store-info">
            <p>You're signing in for: <strong>{storeSlug}</strong></p>
          </div>
        )}
      </>
      )}
      </div>
    </div>
  );
};

export default CustomerAuth;
